import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Assignment as SurveyIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  CardGiftcard as RewardIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const notificationIcons = {
  survey_available: <SurveyIcon color="info" />,
  reward_approved: <MoneyIcon color="success" />,
  moderation_warning: <WarningIcon color="error" />,
  withdrawal_processed: <CheckIcon color="success" />,
  reward_shipped: <RewardIcon color="primary" />
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    email: {
      survey_available: true,
      reward_approved: true,
      moderation_warning: true,
      withdrawal_processed: true
    },
    push: {
      survey_available: true,
      reward_approved: true,
      moderation_warning: true,
      withdrawal_processed: true
    },
    sms: {
      moderation_warning: true,
      withdrawal_processed: true
    }
  });

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      // Load from user profile if available; fallback to defaults
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!currentUser?.id) return;
      const res = await axios.get(`${API_BASE}/users/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.notificationSettings) setSettings(res.data.notificationSettings);
    } catch (e) {
      // ignore for now, use defaults
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    } catch (e) {
      console.error('Failed to mark as read', e);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (e) {
      console.error('Failed to delete notification', e);
    }
  };

  const handleSettingChange = (channel, type) => {
    setSettings(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: !prev[channel][type]
      }
    }));
  };

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/settings`, { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettingsOpen(false);
    } catch (e) {
      console.error('Failed to save settings', e);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString();
    }
    return date.toLocaleDateString();
  };

  return (
    <Box>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mb: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '24px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography 
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#2d3748'
            }}
          >
            Уведомления
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              size="small" 
              onClick={handleMarkAllRead}
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                px: 2,
                boxShadow: '0 4px 15px rgba(9, 132, 227, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                }
              }}
            >
              Отметить все прочитанными
            </Button>
            <IconButton 
              onClick={() => setSettingsOpen(true)}
              sx={{
                color: '#0984e3',
                background: 'rgba(116, 185, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(116, 185, 255, 0.2)'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        <List sx={{ p: 0 }}>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              {index > 0 && <Divider sx={{ opacity: 0.2 }} />}
              <ListItem
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  mb: 1,
                  background: notification.read 
                    ? 'rgba(255, 255, 255, 0.5)' 
                    : 'linear-gradient(135deg, rgba(116, 185, 255, 0.1), rgba(9, 132, 227, 0.05))',
                  border: '1px solid',
                  borderColor: notification.read ? 'rgba(0, 0, 0, 0.05)' : 'rgba(9, 132, 227, 0.2)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <ListItemIcon>
                  {notificationIcons[notification.type]}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#2d3748',
                          fontSize: '1rem'
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Chip
                          label="Новое"
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                            color: '#2d3748',
                            fontWeight: 700,
                            borderRadius: '8px',
                            height: 24
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#718096',
                          mt: 0.5,
                          fontWeight: 500
                        }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#a0aec0',
                          mt: 0.5,
                          display: 'block',
                          fontWeight: 600
                        }}
                      >
                        {formatTimestamp(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!notification.read && (
                    <Button
                      size="small"
                      onClick={() => handleMarkAsRead(notification.id)}
                      sx={{
                        color: '#0984e3',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 2,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.1)'
                        }
                      }}
                    >
                      Отметить прочитанным
                    </Button>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(notification.id)}
                    sx={{
                      color: '#d63031',
                      '&:hover': {
                        background: 'rgba(214, 48, 49, 0.1)'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
          {notifications.length === 0 && (
            <ListItem
              sx={{
                p: 4,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.5)',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      fontSize: '1.1rem'
                    }}
                  >
                    Нет уведомлений
                  </Typography>
                }
                secondary={
                  <Typography
                    sx={{
                      color: '#718096',
                      mt: 1,
                      fontWeight: 500
                    }}
                  >
                    Вы всё просмотрели!
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Notification Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '8px', md: '32px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: '#2d3748',
            fontSize: '1.25rem'
          }}
        >
          Настройки уведомлений
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              mt: 2,
              fontWeight: 700,
              color: '#2d3748',
              fontSize: '1rem'
            }}
          >
            Email уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email.survey_available}
                  onChange={() => handleSettingChange('email', 'survey_available')}
                />
              }
              label="Новые доступные опросы"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email.reward_approved}
                  onChange={() => handleSettingChange('email', 'reward_approved')}
                />
              }
              label="Одобрения опросов и вознаграждения"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email.moderation_warning}
                  onChange={() => handleSettingChange('email', 'moderation_warning')}
                />
              }
              label="Предупреждения модерации"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email.withdrawal_processed}
                  onChange={() => handleSettingChange('email', 'withdrawal_processed')}
                />
              }
              label="Обновления вывода средств"
            />
          </FormGroup>

          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              mt: 3,
              fontWeight: 700,
              color: '#2d3748',
              fontSize: '1rem'
            }}
          >
            Push уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push.survey_available}
                  onChange={() => handleSettingChange('push', 'survey_available')}
                />
              }
              label="Новые доступные опросы"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push.reward_approved}
                  onChange={() => handleSettingChange('push', 'reward_approved')}
                />
              }
              label="Одобрения опросов и вознаграждения"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push.moderation_warning}
                  onChange={() => handleSettingChange('push', 'moderation_warning')}
                />
              }
              label="Предупреждения модерации"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push.withdrawal_processed}
                  onChange={() => handleSettingChange('push', 'withdrawal_processed')}
                />
              }
              label="Обновления вывода средств"
            />
          </FormGroup>

          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              mt: 3,
              fontWeight: 700,
              color: '#2d3748',
              fontSize: '1rem'
            }}
          >
            SMS уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.sms.moderation_warning}
                  onChange={() => handleSettingChange('sms', 'moderation_warning')}
                />
              }
              label="Предупреждения модерации"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.sms.withdrawal_processed}
                  onChange={() => handleSettingChange('sms', 'withdrawal_processed')}
                />
              }
              label="Обновления вывода средств"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setSettingsOpen(false)}
            sx={{
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              px: 3
            }}
          >
            Отмена
          </Button>
          <Button 
            onClick={saveSettings} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              px: 3,
              boxShadow: '0 4px 15px rgba(9, 132, 227, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
              }
            }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}